import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { PublicLangToggle } from "@/components/PublicLangToggle";
import { ArrowRight, ChevronDown, Menu } from "lucide-react";
import { Logo } from "@/components/nav/Brand";

const COPY = {
  en: {
    products: "Products", tools: "Free tools", learn: "Learn", board: "Shariah board",
    pricing: "Pricing", help: "Help", signin: "Sign in", start: "Get started",
    menu: "Menu", menuSub: "Products, free tools and your account",
    p: {
      trade: ["Stocks & ETFs", "Buy whole shares or fractions, cash only"],
      markets: ["Global markets", "70+ exchanges, GCC and worldwide"],
      invest: ["Wealth portfolios", "Ready-made ETF mixes, auto-invested"],
      save: ["USD savings", "Murabaha profit, not interest"],
      halal: ["Islamic investing", "AAOIFI screening, purification, zakat"],
      wallet: ["Multi-currency wallet", "Five currencies, no hidden spread"],
    },
    t: {
      screener: ["Halal screener", "Any stock's verdict, free"],
      rankings: ["Sector rankings", "The cleanest compliant names"],
      lens: ["Chart AI", "Paste a chart, get the read"],
      exchanges: ["Exchange coverage", "Every venue, country and currency"],
      portfolios: ["Portfolio preview", "The five mixes and how they behaved"],
    },
  },
  ar: {
    products: "المنتجات", tools: "أدوات مجانية", learn: "الأكاديمية", board: "الهيئة الشرعية",
    pricing: "الأسعار", help: "المساعدة", signin: "تسجيل الدخول", start: "ابدأ الآن",
    menu: "القائمة", menuSub: "المنتجات والأدوات المجانية وحسابك",
    p: {
      trade: ["الأسهم والصناديق", "اشترِ أسهماً كاملة أو أجزاء، بالنقد فقط"],
      markets: ["الأسواق العالمية", "أكثر من ٧٠ سوقاً، خليجياً وعالمياً"],
      invest: ["محافظ الثروة", "توزيعات جاهزة تُموَّل تلقائياً"],
      save: ["التوفير بالدولار", "ربح مرابحة لا فائدة"],
      halal: ["الاستثمار الإسلامي", "فحص أيوفي والتطهير والزكاة"],
      wallet: ["محفظة متعددة العملات", "خمس عملات بلا فروق خفية"],
    },
    t: {
      screener: ["الفارز الحلال", "حكم أي سهم، مجاناً"],
      rankings: ["ترتيب القطاعات", "أنقى الأسهم المتوافقة"],
      lens: ["عدسة الرسم البياني", "الصق رسماً واحصل على القراءة"],
      exchanges: ["تغطية الأسواق", "كل سوق وبلد وعملة"],
      portfolios: ["معاينة المحافظ", "المحافظ الخمس وكيف تصرّفت"],
    },
  },
};

const PRODUCT_ROUTES = [["trade", "/product/trade"], ["markets", "/product/markets"],
  ["invest", "/product/invest"], ["save", "/product/save"], ["halal", "/product/halal"],
  ["wallet", "/product/wallet"]];
const TOOL_ROUTES = [["screener", "/top-halal-stocks"], ["rankings", "/halal-leaderboards"],
  ["lens", "/chart-lens"], ["exchanges", "/exchanges"], ["portfolios", "/portfolios"]];

const Group = ({ label, items, copy, onPick, testId }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button className="inline-flex items-center gap-1.5 text-base xl:text-[17px] font-medium text-foreground/75 hover:text-[#3E2E65] transition-colors"
        data-testid={testId}>
        {label} <ChevronDown className="h-4 w-4" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-[300px] p-2">
      {items.map(([key, to]) => (
        <DropdownMenuItem key={to} onClick={() => onPick(to)} className="flex-col items-start gap-0.5 rounded-xl py-2.5 cursor-pointer"
          data-testid={`header-link-${key}`}>
          <span className="text-sm font-semibold">{copy[key][0]}</span>
          <span className="text-[11px] text-muted-foreground">{copy[key][1]}</span>
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

/** The header every public page carries: brand, the full product menu, language and sign-up. */
export const PublicHeader = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { lang, user } = useApp();
  const c = COPY[lang === "ar" ? "ar" : "en"];
  const [open, setOpen] = useState(false);
  const go = (to) => { setOpen(false); navigate(to); };
  const LINKS = [[c.learn, "/learn", "header-link-learn"], [c.board, "/shariah-board", "header-link-board"],
    [c.pricing, "/fees", "header-link-pricing"], [c.help, "/help", "header-link-help"]];

  return (
    <header className="sticky top-0 z-50 border-b border-border/70 bg-background/85 backdrop-blur-xl"
      data-testid="public-header">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 h-24 flex items-center gap-6">
        <button onClick={() => navigate("/")} className="flex items-center shrink-0"
          data-testid="header-brand">
          <Logo className="h-11 sm:h-16 w-auto" />
        </button>

        <nav className="hidden xl:flex flex-1 min-w-0 items-center justify-center gap-5 2xl:gap-8"
          data-testid="header-nav">
          <Group label={c.products} items={PRODUCT_ROUTES} copy={c.p} onPick={go} testId="header-products" />
          <Group label={c.tools} items={TOOL_ROUTES} copy={c.t} onPick={go} testId="header-tools" />
          {LINKS.map(([label, to, testid]) => (
            <button key={to} onClick={() => navigate(to)} data-testid={testid}
              className={`text-base xl:text-[17px] font-medium transition-colors ${
                pathname === to ? "text-[#3E2E65]" : "text-foreground/75 hover:text-[#3E2E65]"}`}>
              {label}
            </button>
          ))}
        </nav>

        <div className="ms-auto xl:ms-0 flex items-center gap-2 shrink-0">
          <div className="hidden sm:block">
            <PublicLangToggle onDark={false} />
          </div>
          {user ? (
            <Button className="rounded-full px-5" onClick={() => navigate("/")}
              data-testid="header-dashboard">
              {lang === "ar" ? "لوحتي" : "My dashboard"} <ArrowRight className="h-4 w-4 ms-1" />
            </Button>
          ) : (
            <>
              <Button variant="ghost" className="hidden sm:inline-flex rounded-full"
                onClick={() => navigate("/login")} data-testid="header-signin">{c.signin}</Button>
              <Button className="rounded-full px-5" onClick={() => navigate("/login")}
                data-testid="header-get-started">
                {c.start} <ArrowRight className="h-4 w-4 ms-1" />
              </Button>
            </>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="xl:hidden p-2" aria-label={c.menu} data-testid="header-menu-button">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] p-0 overflow-y-auto" data-testid="header-mobile-menu">
              <SheetHeader className="sr-only">
                <SheetTitle>{c.menu}</SheetTitle>
                <SheetDescription>{c.menuSub}</SheetDescription>
              </SheetHeader>
              <div className="p-5 border-b border-border flex items-center gap-2">
                <Logo className="h-12 w-auto" />
              </div>
              <div className="p-3">
                <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{c.products}</div>
                {PRODUCT_ROUTES.map(([key, to]) => (
                  <button key={to} onClick={() => go(to)} data-testid={`mobile-link-${key}`}
                    className="w-full text-start rounded-xl px-3 py-2 hover:bg-secondary transition-colors">
                    <span className="block text-sm font-medium">{c.p[key][0]}</span>
                    <span className="block text-[11px] text-muted-foreground">{c.p[key][1]}</span>
                  </button>
                ))}
                <div className="px-3 pt-4 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{c.tools}</div>
                {TOOL_ROUTES.map(([key, to]) => (
                  <button key={to} onClick={() => go(to)} data-testid={`mobile-link-${key}`}
                    className="w-full text-start rounded-xl px-3 py-2 hover:bg-secondary transition-colors">
                    <span className="block text-sm font-medium">{c.t[key][0]}</span>
                    <span className="block text-[11px] text-muted-foreground">{c.t[key][1]}</span>
                  </button>
                ))}
                <div className="mt-4 border-t border-border pt-3">
                  {LINKS.map(([label, to, testid]) => (
                    <button key={to} onClick={() => go(to)} data-testid={`mobile-${testid}`}
                      className="w-full text-start rounded-xl px-3 py-2.5 text-sm font-medium hover:bg-secondary transition-colors">
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="p-3 border-t border-border space-y-2">
                <div className="flex justify-center pb-1 sm:hidden">
                  <PublicLangToggle onDark={false} />
                </div>
                <Button className="w-full rounded-full" onClick={() => go(user ? "/" : "/login")}
                  data-testid="mobile-get-started">{user ? (lang === "ar" ? "لوحتي" : "My dashboard") : c.start}</Button>
                {!user && (
                  <Button variant="ghost" className="w-full rounded-full" onClick={() => go("/login")}
                    data-testid="mobile-sign-in">{c.signin}</Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
